import type { en } from './en';

/**
 * Russian strings. Typed as `Record<keyof typeof en, string>` - every key in
 * `en.ts` MUST have an entry here, and vice versa; a mismatch fails `tsc`,
 * not at runtime. Product wording for a seller-facing fulfilment portal, not
 * a literal/machine translation.
 */
export const ru: Record<keyof typeof en, string> = {
  // Nav / shell
  'nav.brand': 'Baltic Fulfilment · Портал',
  'nav.skipToContent': 'Перейти к содержимому',
  'nav.openMenu': 'Открыть меню',
  'nav.closeMenu': 'Закрыть меню',
  'nav.dashboard': 'Дашборд',
  'nav.inventory': 'Остатки',
  'nav.orders': 'Отгрузки',
  'nav.shipments': 'Отгрузки',
  'nav.returns': 'Возвраты',
  'nav.billing': 'Биллинг',
  'nav.logOut': 'Выйти',
  'nav.checkingSession': 'Проверяем сессию…',
  'nav.ownersLinkedOne': 'Подключён {n} продавец',
  'nav.ownersLinkedFew': 'Подключено {n} продавца',
  'nav.ownersLinkedMany': 'Подключено {n} продавцов',
  'nav.ownersLinkedOther': 'Подключено {n} продавца',

  // Common
  'common.loading': 'Загрузка…',
  'common.retry': 'Повторить',
  'common.saving': 'Сохранение…',
  'common.of': 'из',
  'common.previous': 'Назад',
  'common.next': 'Далее',
  'common.nothingHereYet': 'Здесь пока пусто',
  'common.notFound': 'Не найдено',
  'common.notFoundHint': 'Эта запись не существует или недоступна для вашей учётной записи.',
  'filters.aria': 'Фильтры списка',
  'filters.all': 'Все',
  'filters.client': 'Клиент',
  'filters.allClients': 'Все клиенты',
  'filters.allWarehouses': 'Все склады',
  'filters.from': 'С',
  'filters.to': 'По',
  'filters.sort': 'Сортировка по дате',
  'filters.newest': 'Сначала новые',
  'filters.oldest': 'Сначала старые',
  'filters.reset': 'Сбросить фильтры',
  'filters.rangeInvalid': 'Дата начала позже даты окончания: применяется только дата начала.',
  'filters.noMatch': 'По этим фильтрам ничего не найдено.',
  'common.status': 'Статус',
  'common.total': 'Итого',
  'common.yes': 'Да',
  'common.no': 'Нет',
  'common.search': 'Поиск…',
  'common.hide': 'Скрыть',
  'common.note': 'Примечание',

  // Generic errors
  'error.unexpected': 'Произошла непредвиденная ошибка. Попробуйте ещё раз или вернитесь позже.',
  'error.sessionExpired': 'Сессия больше не действительна. Войдите заново.',
  'error.tooManyRequests': 'Слишком много запросов — подождите немного и попробуйте снова.',
  'error.serverUnexpected': 'На сервере произошла непредвиденная ошибка.',
  'error.requestFailed': 'Не удалось выполнить запрос.',

  // Status / domain-state labels
  'status.DRAFT': 'Черновик',
  'status.BUILDING': 'Формируется',
  'status.PICKING': 'Сборка',
  'status.PICKED': 'Собран',
  'status.PACKING': 'Упаковка',
  'status.PACKED': 'Упакован',
  'status.READY': 'Готов к отгрузке',
  'status.SHIPPED': 'Отгружен',
  'status.CANCELLED': 'Отменён',
  'status.CANCELLED_AFTER_PICK': 'Отменён (после сборки)',
  'status.NEW': 'Новый',
  'status.RESERVED': 'Зарезервирован',
  'status.ARRIVED': 'Прибыл',
  'status.RECEIVING': 'Приёмка',
  'status.RECEIVED': 'Принят',
  'status.CLOSED': 'Закрыт',
  'status.DEPARTED': 'Отправлено',
  'status.ACCEPTED': 'Принято',
  'status.REJECTED': 'Отклонено',
  'status.NONE': 'Нет',
  'status.SHORT': 'Недостача',
  'status.OVER': 'Излишек',
  'status.DAMAGED': 'Повреждён',
  'status.MISMATCH': 'Несоответствие',
  'status.RESTOCK': 'Возвращён на склад',
  'status.QUARANTINE': 'На карантине',
  'status.WRITE_OFF': 'Списан',
  'status.AWAITING_DISPOSITION': 'Ожидает решения',
  'status.ISSUED': 'Выставлен',
  'status.PAID': 'Оплачен',
  'status.PARTIALLY_PAID': 'Частично оплачен',
  'status.OVERDUE': 'Просрочен',
  'status.SENT': 'Отправлен',
  'status.DISPUTED': 'Оспорен',
  'status.VOID': 'Аннулирован',
  'status.RECEIVING_UNIT': 'Приёмка',
  'status.RECEIVING_DOC': 'Документ приёмки',
  'status.PICKING_UNIT': 'Сборка',
  'status.PACKING_UNIT': 'Упаковка',
  'status.PACKAGE_MATERIAL': 'Упаковочный материал',
  'status.PALLET_HANDLING': 'Работа с паллетой',
  'status.STORAGE_LOCATION': 'Хранение',
  'status.RETURN_UNIT': 'Возврат',
  'status.MARKING_SCAN': 'Сканирование маркировки',
  'status.RELABEL': 'Переэтикетирование',
  'status.DISPOSAL': 'Утилизация',
  'status.MANUAL_HANDLING': 'Ручная обработка',
  'status.RECEIVING_DISCREPANCY': 'Расхождение при приёмке',
  'status.DAMAGE': 'Повреждение',
  'status.UNKNOWN_ITEM': 'Неопознанный товар',
  'status.RETURN_INSPECTION': 'Осмотр возврата',
  'status.INBOUND_LINE': 'строка приёмки',
  'status.SALES_ORDER': 'заказ',
  'status.RETURN_DISPOSITION': 'решение по возврату',
  'status.PACKAGE': 'коробка',
  'status.BANK_TRANSFER': 'Банковский перевод',
  'status.CARD': 'Карта',
  'status.CASH': 'Наличные',
  'status.OTHER': 'Другое',

  // Login
  'login.title': 'Вход в личный кабинет продавца',
  'login.subtitle': 'Используйте email и пароль, которые вам предоставил ваш фулфилмент-партнёр.',
  'login.email': 'Email',
  'login.password': 'Пароль',
  'login.signingIn': 'Выполняется вход…',
  'login.signIn': 'Войти',
  'login.forgotPassword': 'Забыли пароль?',
  'login.incorrectCredentials': 'Неверный email или пароль.',
  'login.couldNotSignIn': 'Не удалось войти. Попробуйте снова.',

  // Reset password
  'resetPassword.title': 'Сброс пароля',
  'resetPassword.requestDone': 'Если аккаунт с таким email существует, на него отправлена ссылка для сброса пароля. Перейдите по ней, чтобы задать новый пароль.',
  'resetPassword.requestHint': 'Укажите email вашей учётной записи, и мы отправим ссылку для сброса пароля.',
  'resetPassword.sending': 'Отправляем…',
  'resetPassword.sendResetLink': 'Отправить ссылку для сброса',
  'resetPassword.backToSignIn': 'Назад ко входу',
  'resetPassword.requestNewLink': 'Запросить новую ссылку',
  'resetPassword.passwordUpdated': 'Пароль обновлён',
  'resetPassword.passwordUpdatedHint': 'Ваш пароль изменён. Все остальные активные сессии завершены.',
  'resetPassword.chooseNewPassword': 'Задайте новый пароль',
  'resetPassword.newPassword': 'Новый пароль',
  'resetPassword.confirmPassword': 'Подтвердите пароль',
  'resetPassword.passwordMismatch': 'Пароли не совпадают.',
  'resetPassword.setNewPassword': 'Установить новый пароль',
  'resetPassword.tokenExpired': 'Срок действия этой ссылки истёк. Запросите новую.',
  'resetPassword.tokenUsed': 'Эта ссылка уже была использована. Запросите новую.',
  'resetPassword.tokenInvalid': 'Эта ссылка недействительна.',
  'resetPassword.couldNotReset': 'Не удалось сбросить пароль. Попробуйте снова.',
  'resetPassword.checkingLink': 'Проверяем вашу ссылку…',
  'resetPassword.linkExpiredOrUsed': 'Ссылка недействительна или уже использована',
  'resetPassword.setUpAccount': 'Настройте свой аккаунт',
  'resetPassword.setPassword': 'Задать пароль',
  'resetPassword.accountActivated': 'Аккаунт активирован',
  'resetPassword.accountActivatedHint': 'Ваш аккаунт продавца готов к работе. Все остальные активные сессии завершены.',

  // Dashboard
  'dashboard.loading': 'Загружаем ваш дашборд…',
  'dashboard.skusInStock': 'SKU на складе',
  'dashboard.unitsOnHand': 'Физически на складе',
  'dashboard.unitsAvailable': 'Доступно к продаже',
  'dashboard.unitsQuarantine': 'На карантине',
  'dashboard.unitsInProcess': 'В обработке (приёмка/сборка/упаковка)',
  'dashboard.unitsShipped': 'Отгружено (не в наличии)',
  'inventory.colBucket': 'Статус запаса',
  'inventory.bucket.BIN': 'В ячейке',
  'inventory.bucket.QUARANTINE': 'Карантин',
  'inventory.bucket.IN_PROCESS': 'В обработке',
  'inventory.bucket.SHIPPED': 'Отгружено (вне склада)',
  'inventory.summary.title': 'Сводка по запасам',
  'inventory.summary.inBins': 'Физически в ячейках',
  'inventory.summary.available': 'Доступно (к отбору за вычетом резерва)',
  'inventory.summary.quarantine': 'Карантин',
  'inventory.summary.inProcess': 'В обработке',
  'inventory.summary.shipped': 'Отгружено (не в наличии)',
  'dashboard.unitsReserved': 'Единиц в резерве',
  'dashboard.openOrders': 'Открытые заказы',
  'dashboard.ordersNeedingAttention': 'Заказы, требующие внимания',
  'dashboard.unpaidInvoices': 'Неоплаченные счета',
  'dashboard.unpaidOverdueInvoices': 'Неоплаченные и просроченные счета',
  'dashboard.nothingOutstanding': 'Задолженностей нет.',
  'dashboard.colInvoice': 'Счёт',
  'dashboard.colDue': 'Срок оплаты',
  'dashboard.colBalance': 'Остаток',
  'dashboard.draft': '(черновик)',
  'dashboard.recentCharges': 'Последние начисления',
  'dashboard.noChargesYet': 'Начислений пока нет.',
  'dashboard.colKind': 'Тип',
  'dashboard.colOccurred': 'Дата',
  'dashboard.colAmount': 'Сумма',
  'dashboard.recentReturns': 'Последние возвраты',
  'dashboard.noRecentReturns': 'Возвратов пока нет.',
  'dashboard.colReturn': 'Возврат',
  'dashboard.discrepancy': 'Расхождение',
  'dashboard.colCreated': 'Создан',
  'dashboard.recentEvidence': 'Последние фото',
  'dashboard.noEvidenceYet': 'Фото пока нет.',
  'dashboard.evidenceLine': '{kind}, {entity} — {date}',

  // Inventory
  'inventory.colName': 'Название',
  'inventory.colLocation': 'Ячейка',
  'inventory.colOnHand': 'На складе',
  'inventory.colReserved': 'В резерве',
  'inventory.colAvailable': 'Доступно',
  'inventory.colSerialTracked': 'Серийный учёт',
  'inventory.skuOrName': 'SKU или название',
  'inventory.emptyTitle': 'Остатки не найдены',
  'inventory.emptyHintSearch': 'Попробуйте изменить запрос.',
  'inventory.emptyHintNoStock': 'По вашему аккаунту ещё не поступили остатки.',

  // Orders list
  'ordersPage.colOrder': 'Заказ',
  'ordersPage.colChannel': 'Канал',
  'ordersPage.colDue': 'Срок',
  'ordersPage.colOrdered': 'Заказано',
  'ordersPage.colShipped': 'Отгружено',
  'ordersPage.allStatuses': 'Все',
  'ordersPage.emptyTitle': 'Отгрузок пока нет',

  // Order detail
  'orderDetail.title': 'Заказ {number}',
  'orderDetail.backToOrders': '← Назад к отгрузкам',
  'orderDetail.warehouse': 'Склад',
  'orderDetail.lines': 'Строки',
  'orderDetail.picked': 'Собрано',
  'orderDetail.packed': 'Упаковано',
  'orderDetail.evidence': 'Фото',
  'orderCancel.open': 'Отменить заявку',
  'orderCancel.confirm': 'Заявка будет отменена, а зарезервированный товар освобождён. Это действие нельзя отменить.',
  'orderCancel.yes': 'Да, отменить заявку',
  'orderCancel.no': 'Оставить заявку',
  'orderCancel.busy': 'Отменяем…',
  'orderCancel.done': 'Заявка отменена. Зарезервированный товар освобождён.',
  'orderCancel.pickingHint': 'Сборка уже началась — чтобы отменить, свяжитесь со складом',
  'orderCancel.err.cannot': 'Склад уже начал работу с этой заявкой — свяжитесь со складом',
  'orderCancel.err.channel': 'Заказы маркетплейса отменяются в самом маркетплейсе, а не здесь.',
  'orderCancel.err.notFound': 'Заявка не найдена.',
  'orderCancel.err.tooMany': 'Слишком много запросов за короткое время. Подождите минуту и попробуйте снова.',
  'orderCancel.err.network': 'Связь прервалась, и мы не смогли подтвердить отмену. Проверьте статус ниже и при необходимости повторите.',
  'orderCancel.err.other': 'Не удалось отменить заявку. Попробуйте ещё раз.',
  'orderDetail.lineCancelled': 'отменена',

  // Evidence gallery
  'evidence.loading': 'Загружаем фото…',
  'evidence.couldNotLoad': 'Не удалось загрузить фото.',
  'evidence.noneUploaded': 'Фото пока не загружены.',
  'evidence.view': 'Открыть',
  'evidence.altText': 'Фото: {kind}',

  // Shipments
  'shipmentsPage.colShipment': 'Отгрузка',
  'shipmentsPage.colCarrier': 'Перевозчик',
  'shipmentsPage.colYourPackages': 'Ваши коробки',
  'shipmentsPage.colDeparted': 'Отправлена',
  'shipmentsPage.emptyTitle': 'Отгрузок пока нет',

  // Returns
  'returnsPage.colReturn': 'Возврат',
  'returnsPage.colLines': 'Строки',
  'returnsPage.colDiscrepancies': 'Расхождения',
  'returnsPage.colArrived': 'Прибыл',
  'returnsPage.emptyTitle': 'Возвратов пока нет',

  // Return detail
  'returnDetail.title': 'Возврат {number}',
  'returnDetail.backToReturns': '← Назад к возвратам',
  'returnDetail.arrived': 'Прибыл',
  'returnDetail.unknownSku': '(SKU неизвестен)',
  'returnDetail.expected': 'Ожидалось',
  'returnDetail.received': 'Принято',
  'returnDetail.disposition': 'Решение',
  'returnDetail.qty': 'Кол-во',

  // Billing (shared)
  'billing.colKind': 'Тип',
  'billing.colAmount': 'Сумма',
  'billing.downloadPdf': 'Скачать PDF',

  // Billing page (tabs)
  'billingPage.invoices': 'Счета',
  'billingPage.charges': 'Начисления',

  // Invoices tab
  'invoicesTab.downloadFailed': 'Не удалось скачать PDF. Попробуйте снова.',
  'invoicesTab.noInvoicesYet': 'Счетов пока нет.',
  'invoicesTab.colInvoiceNumber': '№ счёта',
  'invoicesTab.colPeriod': 'Период',
  'invoicesTab.invoiceTitle': 'Счёт {number}',
  'invoicesTab.preparing': 'Готовим…',
  'invoicesTab.close': 'Закрыть',
  'invoicesTab.colRate': 'Ставка',
  'invoicesTab.payments': 'Платежи',
  'invoicesTab.paymentLine': '{method} — {amount}, {date}',

  // Charges tab
  'chargesTab.noChargesYet': 'Начислений пока нет.',
  'chargesTab.unit': 'Единица',
  // Seller self-service requests (Stage 1)
  'nav.inbound': 'Приёмка',
  'status.EXPECTED': 'Ожидается',
  'requests.in.title': 'Новая заявка на приёмку',
  'requests.out.title': 'Новая заявка на отгрузку',
  'requests.in.subtitle': 'Сообщите складу, что вы отправляете. В остаток ничего не добавляется, пока склад не примет товар.',
  'requests.out.subtitle': 'Попросите склад отправить ваш товар получателю. Товар резервируется сразу при создании заявки.',
  'requests.company': 'Компания',
  'requests.chooseCompany': 'Выберите компанию',
  'requests.warehouse': 'Склад',
  'requests.chooseWarehouse': 'Выберите склад',
  'requests.changeResets': 'При смене компании или склада список товаров очищается.',
  'requests.chooseWarehouseFirst': 'Выберите склад, чтобы добавить товары.',
  'requests.items': 'Товары',
  'requests.findItem': 'Найдите товар (артикул, название или штрихкод)',
  'sku.article': 'Артикул',
  'sku.size': 'Размер',
  'sku.color': 'Цвет',
  'sku.barcode': 'Штрихкод',
  'sku.barcodes': 'Штрихкоды',
  'requests.itemResults': 'Найденные товары',
  'requests.noItemsFound': 'Среди ваших товаров совпадений нет.',
  'requests.available': 'доступно {qty}',
  'requests.add': 'Добавить',
  'requests.addItem': 'Добавить {sku}',
  'requests.lineMerged': '{sku} уже в заявке — количество увеличено.',
  'requests.excelTitle': 'Или заполните файл Excel',
  'requests.excelHint': 'Скачайте шаблон, заполните по строке на товар и загрузите. Каждая строка проверяется до создания заявки.',
  'requests.in.template': 'Скачать шаблон приёмки',
  'requests.out.template': 'Скачать шаблон отгрузки',
  'requests.uploadExcel': 'Загрузить файл Excel',
  'requests.checkingFile': 'Проверяем файл…',
  'requests.previewTitle': 'Проверка файла',
  'requests.previewOk': 'Все строки ({rows}) в порядке: товаров к добавлению — {lines}.',
  'requests.previewErrors': 'Нужно исправить строки: {errors} из {rows}. Исправьте их в файле и загрузите снова — ничего не создано.',
  'requests.rowN': 'Строка {n}',
  'requests.useFile': 'Перенести товары из файла ({n})',
  'requests.replaceWithFile': 'Заменить товары на {n} из файла',
  'requests.fileApplied': 'Товары из файла ({n}) добавлены в заявку. Проверьте количества ниже.',
  'requests.row.ok': 'OK',
  'requests.row.merged': 'Тот же товар, что в строке {row} — количества сложены',
  'requests.row.formula': 'Содержит формулу — вставьте значение',
  'requests.row.identifierMissing': 'Укажите артикул или штрихкод',
  'requests.row.qtyInvalid': 'Количество — целое число больше нуля',
  'requests.row.skuNotFound': 'В вашей номенклатуре нет товара с артикулом {code}',
  'requests.row.barcodeNotFound': 'В вашей номенклатуре нет товара со штрихкодом {barcode}',
  'requests.row.barcodeAmbiguous': 'Штрихкод {barcode} есть у нескольких ваших товаров — укажите артикул',
  'requests.row.mismatch': 'Артикул {code} и штрихкод {barcode} — разные товары',
  'requests.row.insufficient': 'Доступно только {available} шт. {code}, запрошено {requested}',
  'requests.file.notXlsx': 'Это не файл .xlsx. Скачайте шаблон и заполните его.',
  'requests.file.tooLarge': 'Файл слишком большой (максимум 512 КБ).',
  'requests.file.macros': 'Файлы с макросами не принимаются. Сохраните как обычный .xlsx.',
  'requests.file.externalLinks': 'Файл ссылается на другие файлы. Вставьте значения и сохраните как обычный .xlsx.',
  'requests.file.embedded': 'Файл содержит встроенные объекты. Сохраните как обычный .xlsx.',
  'requests.file.corrupt': 'Не удалось прочитать файл. Скачайте шаблон и заполните его.',
  'requests.file.tooManyRows': 'Слишком много строк — не больше {max} товаров в одной заявке.',
  'requests.file.noSheet': 'В файле нет листа с данными.',
  'requests.file.headerNotFound': 'Не найдены заголовки. Оставьте заголовки шаблона «Артикул / Штрихкод / Количество».',
  'requests.file.empty': 'В файле нет строк с товарами.',
  'requests.lines': 'Строки заявки',
  'requests.noLines': 'Товаров пока нет — найдите товар выше или загрузите файл.',
  'requests.qtyFor': 'Количество для {sku}',
  'requests.qtyInvalid': 'Введите целое число больше нуля.',
  'requests.remove': 'Удалить',
  'requests.removeLine': 'Удалить {sku}',
  'requests.itemsSummary': 'Позиций: {lines}, всего штук: {units}',
  'requests.in.details': 'Данные поставки',
  'requests.out.details': 'Данные заказа',
  'requests.in.reference': 'Ваш номер поставки (необязательно)',
  'requests.out.reference': 'Ваш номер заказа (необязательно)',
  'requests.referenceHint': 'Мы добавим код вашей компании в начале и короткий код в конце (например, ACME-PO-123-7KQ2). Оставьте пустым — номер присвоится автоматически.',
  'requests.in.date': 'Ожидаемая дата прибытия (необязательно)',
  'requests.out.date': 'Отгрузить до (необязательно)',
  'requests.out.recipient': 'Получатель',
  'requests.out.name': 'Имя получателя',
  'requests.out.phone': 'Телефон',
  'requests.out.address': 'Адрес',
  'requests.out.city': 'Город',
  'requests.out.postalCode': 'Индекс',
  'requests.out.country': 'Страна',
  'requests.out.comment': 'Комментарий для склада (необязательно)',
  'requests.fillToContinue': 'Выберите склад, добавьте хотя бы один товар с корректным количеством и заполните обязательные поля.',
  'requests.review': 'Проверить заявку',
  'requests.reviewTitle': 'Проверьте перед отправкой',
  'requests.whatHappens': 'Что произойдёт:',
  'requests.in.whatHappens': 'склад получит ожидаемую поставку. Ваш остаток не изменится, пока склад не примет и не пересчитает товар.',
  'requests.out.whatHappens': 'создаётся заказ, и товар сразу резервируется под него — для других заказов он больше не доступен. Затем склад соберёт, упакует и отправит заказ.',
  'requests.in.submit': 'Отправить в работу',
  'requests.out.submit': 'Отправить в работу',
  'requests.sending': 'Отправляем…',
  'requests.backToEdit': 'Вернуться к редактированию',
  'requests.in.done': 'Заявка на приёмку {number} отправлена складу.',
  'requests.in.doneHint': 'Статус: «Ожидается». Остаток увеличится только после приёмки товара складом.',
  'requests.out.done': 'Заявка {number} отправлена складу, товар зарезервирован.',
  'requests.out.doneHint': 'Она ещё не отгружена — следите за статусом в разделе «Отгрузки».',
  'requests.openCreated': 'Открыть заявку',
  'requests.createAnother': 'Создать ещё одну',
  'requests.noWarehouseTitle': 'Для вашего аккаунта пока не подключён склад',
  'requests.noWarehouseHint': 'Заявки можно отправлять, когда склад обслуживает ваш аккаунт. Свяжитесь со складом.',
  'requests.err.network': 'Связь прервалась, и мы не смогли подтвердить заявку. Дубля не будет — её можно безопасно отправить ещё раз.',
  'requests.err.insufficient': 'Недостаточно товара {sku}: доступно {available}, запрошено {requested}. Ничего не зарезервировано.',
  'requests.err.ownerNotAllowed': 'Эта компания не привязана к вашему входу.',
  'requests.err.ownerRequired': 'Выберите компанию, от которой создаётся заявка.',
  'requests.err.warehouseNotAllowed': 'Этот склад не обслуживает ваш аккаунт.',
  'requests.err.tooMany': 'Слишком много запросов за короткое время. Ничего не создано — подождите {seconds} с и нажмите кнопку ещё раз; всё введённое сохранено.',
  'requests.err.numberTaken': 'Такой номер уже используется. Укажите другой или оставьте поле пустым.',
  'requests.err.notCancellable': 'Эту заявку уже нельзя отозвать — склад начал работу с ней. Свяжитесь со складом.',
  'requests.err.referenceInvalid': 'Номер может содержать буквы, цифры, пробелы и . _ - / # (до 40 символов).',
  'requests.err.skuNotYours': 'Один из товаров не из вашей номенклатуры.',
  'requests.err.qtyInvalid': 'Каждое количество — целое число больше нуля.',
  'requests.err.noLines': 'Добавьте хотя бы один товар.',
  'requests.err.checkFields': 'Некоторые поля не заполнены или заполнены неверно. Проверьте заявку.',
  'requests.in.new': 'Новая заявка на приёмку',
  'requests.out.new': 'Новая заявка на отгрузку',
  'requests.in.colNumber': 'Поставка',
  'requests.in.colExpected': 'Ожидается, шт',
  'requests.in.colReceived': 'Принято, шт',
  'requests.in.colCreated': 'Создана',
  'requests.in.colSource': 'Кто создал',
  'requests.in.sourcePortal': 'Вы, в кабинете',
  'requests.in.sourceWarehouse': 'Склад',
  'requests.in.emptyTitle': 'В приёмке пока ничего нет',
  'requests.in.emptyHint': 'Создайте заявку на приёмку, чтобы сообщить складу, что вы отправляете.',
  'requests.in.detailTitle': 'Приёмка {number}',
  'requests.in.back': '← К приёмке',
  'requests.in.cancel': 'Отозвать заявку',
  'requests.in.cancelConfirm': 'Отозвать заявку? Склад перестанет ожидать этот товар.',
  'requests.in.expectedReceived': 'ожидается {expected}, принято {received}',
  'requests.in.stateHint.EXPECTED': 'Склад ожидает этот товар. Ваш остаток пока не изменился.',
  'requests.in.stateHint.ARRIVED': 'Товар прибыл на склад и ожидает приёмки.',
  'requests.in.stateHint.RECEIVING': 'Склад принимает и пересчитывает товар.',
  'requests.in.stateHint.RECEIVED': 'Принято. Пересчитанный товар добавлен в ваш остаток.',
  'requests.in.stateHint.CLOSED': 'Принято и закрыто.',
  'requests.in.stateHint.CANCELLED': 'Отозвана — склад больше не ожидает этот товар.',
  // Products (SKU import)
  'catalog.listLabel': 'Каталог товаров',
  'catalog.search': 'Поиск по артикулу, названию или штрихкоду',
  'catalog.col.article': 'Артикул',
  'catalog.col.name': 'Наименование',
  'catalog.col.size': 'Размер',
  'catalog.col.color': 'Цвет',
  'catalog.col.barcode': 'Штрихкод',
  'catalog.col.weight': 'Вес, кг',
  'catalog.col.dims': 'Д×Ш×В, см',
  'catalog.empty.title': 'В каталоге пока нет товаров',
  'catalog.empty.hint': 'Скачайте шаблон, заполните его и загрузите кнопкой «Загрузить Excel» — товары сразу появятся здесь.',
  'catalog.emptySearch.title': 'Ничего не найдено',
  'catalog.emptySearch.hint': 'Попробуйте другой артикул, название или штрихкод.',
  'catalog.imported': 'Только что загружено',
  'catalog.afterImport': 'Загруженные товары выделены в списке ниже.',
  'skuImport.col.size': 'Размер',
  'skuImport.col.color': 'Цвет',
  'skuImport.row.variant': 'Для этой комбинации размера и цвета получился тот же код, что у другого товара. Измените артикул или добавьте столбец «Код SKU».',
  'skuImport.subtitle': 'Ваш каталог: все товары и размеры. Добавляйте и обновляйте его из файла Excel — ничего не сохранится, пока вы не подтвердите.',
  'skuImport.templateHint': 'Одна строка — один товар; для одежды — одна строка на размер и цвет (артикул повторяется). Артикул и наименование обязательны. Вес в кг, размеры в см. До 500 строк в файле; каждая строка проверяется до того, как что-либо сохранится.',
  'skuImport.downloadTemplate': 'Скачать шаблон',
  'skuImport.onExisting': 'Товары, которые уже есть',
  'skuImport.onExisting.skip': 'Оставить существующие товары без изменений',
  'skuImport.onExisting.update': 'Обновить существующие товары (название, вес, размеры)',
  'skuImport.upload': 'Загрузить Excel',
  'skuImport.checking': 'Проверяем файл…',
  'skuImport.previewTitle': 'Результат проверки',
  'skuImport.summary': '{new} новых, {update} к обновлению, {skip} без изменений, {error} с ошибками',
  'skuImport.nothingSaved': 'Пока ничего не сохранено. Нажмите кнопку ниже, чтобы импортировать.',
  'skuImport.hasErrors': 'В некоторых строках есть ошибки, поэтому импорт невозможен. Исправьте отмеченные строки в файле и загрузите его снова.',
  'skuImport.alreadyImported': 'Этот файл уже был импортирован, делать ничего не нужно.',
  'skuImport.col.row': 'Строка',
  'skuImport.col.code': 'Артикул / код',
  'skuImport.col.name': 'Наименование',
  'skuImport.col.barcode': 'Штрихкод',
  'skuImport.col.status': 'Статус',
  'skuImport.status.NEW': 'Новый',
  'skuImport.status.UPDATE': 'Обновление',
  'skuImport.status.SKIP': 'Без изменений',
  'skuImport.status.ERROR': 'Ошибка',
  'skuImport.import': 'Импортировать товары: {n}',
  'skuImport.importing': 'Импортируем…',
  'skuImport.uploadAnother': 'Загрузить другой файл',
  'skuImport.done': 'Импортировано: {created} новых, {updated} обновлено.',
  'skuImport.doneSkipped': '{skipped} без изменений.',
  'skuImport.doneReplayed': 'Этот импорт уже был выполнен ранее.',
  'skuImport.row.code': 'Артикул не указан или длиннее 64 символов.',
  'skuImport.row.name': 'Наименование не указано или длиннее 200 символов.',
  'skuImport.row.weight': 'Вес должен быть числом в килограммах больше нуля (например, 0,35).',
  'skuImport.row.length': 'Длина должна быть числом в сантиметрах больше нуля (например, 12,5).',
  'skuImport.row.width': 'Ширина должна быть числом в сантиметрах больше нуля (например, 12,5).',
  'skuImport.row.height': 'Высота должна быть числом в сантиметрах больше нуля (например, 12,5).',
  'skuImport.row.barcode': 'Штрихкод указан неверно.',
  'skuImport.row.size': 'Размер указан слишком длинно.',
  'skuImport.row.color': 'Цвет указан слишком длинно.',
  'skuImport.row.duplicateKey': 'Такой же вариант (артикул + размер + цвет) или штрихкод встречается в файле больше одного раза.',
  'skuImport.row.duplicateBarcode': 'Этот штрихкод уже есть у другого товара.',
  'skuImport.row.generic': 'Эту строку нельзя импортировать.',
  'skuImport.file.notXlsx': 'Выберите файл Excel в формате .xlsx.',
  'skuImport.file.tooLarge': 'Файл слишком большой (максимум 512 КБ).',
  'skuImport.file.macros': 'В файле есть макросы. Сохраните его как обычный .xlsx без макросов.',
  'skuImport.file.externalLinks': 'В файле есть ссылки на другие файлы. Уберите внешние ссылки и повторите.',
  'skuImport.file.embedded': 'В файле есть встроенные объекты. Уберите их и повторите.',
  'skuImport.file.corrupt': 'Файл не удалось прочитать. Убедитесь, что это корректный файл .xlsx.',
  'skuImport.file.tooManyRows': 'Слишком много строк — не больше {max} товаров в одном файле.',
  'skuImport.file.headerNotFound': 'Не найдена строка заголовков. Используйте шаблон: нужны колонки «Артикул» и «Наименование».',
  'skuImport.file.empty': 'В файле нет товаров.',
  'skuImport.file.formula': 'В строке {row} есть формула. Замените формулы обычными значениями и повторите.',
  'skuImport.file.noSheet': 'В файле нет листа для чтения.',
  'skuImport.file.alreadyUsed': 'Этот файл уже использовался для импорта.',
  'skuImport.err.hasErrors': 'В файле есть строки с ошибками, поэтому импорт невозможен. Исправьте файл и загрузите его снова.',
  'skuImport.err.stale': 'Номенклатура изменилась после проверки — загрузите файл ещё раз.',
  'skuImport.err.partial': 'Часть товаров не удалось сохранить. Загрузите файл ещё раз, чтобы увидеть, что осталось импортировать.',
  'skuImport.err.tooMany': 'Слишком много проверок за короткое время. Попробуйте через минуту.',
  'skuImport.err.network': 'Соединение потеряно. Проверьте интернет и повторите.',
  'skuImport.err.generic': 'Что-то пошло не так. Повторите чуть позже.',
  'nav.products': 'Товары',
  'skuImport.title': 'Товары',
  'skuImport.company': 'Компания',
  'skuImport.chooseCompany': 'Выберите компанию',
  'skuImport.companyRequired': 'Сначала выберите компанию, затем загрузите файл.',
  'skuImport.noCompanyTitle': 'К вашей учётной записи не привязана ни одна компания',
  'skuImport.noCompanyHint': 'Попросите менеджера привязать компанию и вернитесь сюда.',
  'skuImport.err.ownerNotAllowed': 'Вы не можете импортировать товары в эту компанию.',
  'mpImport.open': 'Импортировать из маркетплейса',
  'mpImport.title': 'Импорт из маркетплейса',
  'mpImport.hint': 'Выберите подключённый маркетплейс. Мы прочитаем ваши карточки товаров и покажем, что будет создано и обновлено. Пока вы не подтвердите, ничего не сохраняется.',
  'mpImport.loading': 'Загружаем ваши маркетплейсы…',
  'mpImport.noneTitle': 'Маркетплейс не подключён',
  'mpImport.noneHint': 'Сначала подключите маркетплейс в разделе «Интеграции», затем вернитесь сюда.',
  'mpImport.unavailable': 'Импорт товаров из этого маркетплейса пока недоступен. Используйте шаблон Excel.',
  'mpImport.choose': 'Маркетплейс',
  'mpImport.fetch': 'Загрузить товары',
  'mpImport.fetching': 'Читаем данные маркетплейса…',
  'mpImport.next': 'Загрузить следующие товары',
  'mpImport.more': 'В маркетплейсе есть ещё товары. Подтвердите этот импорт, затем загрузите следующие.',
  'mpImport.hasErrors': 'У некоторых карточек есть ошибки — они не будут импортированы. Остальные можно импортировать; исправьте эти карточки в маркетплейсе и загрузите товары снова.',
  'mpImport.summary': 'Найдено карточек: {cards}, вариантов: {variants}. Новых: {new}, к обновлению: {update}, без изменений: {skip}, с ошибками: {error}.',
  'mpImport.notFound': 'В маркетплейсе не найдено: {n}. Эти товары остаются в каталоге без изменений.',
  'mpImport.renamed': 'Будут переименованы: {n}. Название и код обновятся по данным маркетплейса; чтобы оставить как есть, выберите «Оставить существующие товары без изменений».',
  'mpImport.lastCheck': 'Подключение проверено: {when}',
  'mpImport.row.noBarcode': 'Карточка артикул {article}: нет штрихкода.',
  'mpImport.row.badCard': 'Карточка {article}: некорректный идентификатор, пропущена.',
  'mpImport.row.noArticle': 'У карточки нет артикула.',
  'mpImport.done': 'Импорт завершён: добавлено {created}, обновлено {updated}, пропущено {skipped}.',
  'mpImport.toCatalog': 'Перейти к товарам',
  'mpImport.err.noContentAccess': 'У токена нет доступа к категории «Контент». Создайте токен в личном кабинете WB с категорией «Контент» (и «Маркетплейс») и обновите ключ в разделе «Интеграции».',
  'mpImport.alreadyImported': 'Эти товары уже импортированы, делать ничего не нужно.',
  'mpImport.discard': 'Отменить проверку',
  'mpImport.close': 'Закрыть',
  'mpImport.empty': 'В маркетплейсе не найдено товаров.',
  'mpImport.tooMany': 'Маркетплейс вернул больше {max} товаров сразу. Обратитесь к менеджеру.',
  'mpImport.err.notAvailable': 'Импорт товаров из этого маркетплейса пока недоступен. Используйте шаблон Excel.',
  'mpImport.err.keyRejected': 'Маркетплейс не принял сохранённый ключ. Обновите его в разделе «Интеграции».',
  'mpImport.err.rateLimited': 'Маркетплейс просит подождать, повторите через минуту.',
  'mpImport.err.down': 'Не удалось связаться с маркетплейсом. Повторите позже.',
  'mpImport.err.notConnected': 'Этот маркетплейс больше не подключён.',
  'skuImport.err.ownerRequired': 'Сначала выберите компанию.',
  // Integrations
  'nav.integrations': 'Интеграции',
  'integrations.title': 'Интеграции',
  'integrations.subtitle': 'Подключите свои аккаунты маркетплейсов. Каждое подключение проверяется настоящим запросом к маркетплейсу.',
  'integrations.loadError': 'Не удалось загрузить интеграции. Проверьте соединение и попробуйте снова.',
  'integrations.noOwnersTitle': 'К вашей учётной записи не привязана ни одна компания',
  'integrations.noOwnersHint': 'Попросите менеджера привязать компанию и вернитесь сюда.',
  'integrations.kind.WB': 'Wildberries',
  'integrations.kind.OZON': 'Ozon',
  'integrations.kind.YANDEX_MARKET': 'Яндекс Маркет',
  'integrations.state.NOT_CONNECTED': 'Не подключено',
  'integrations.state.UNVERIFIED': 'Ключ сохранён, ещё не проверен',
  'integrations.state.CONNECTED': 'Подключено · проверено {date}',
  'integrations.state.CONNECTED_NO_DATE': 'Подключено',
  'integrations.state.NEEDS_ATTENTION': 'Требует внимания',
  'integrations.attention.UNAUTHORISED': 'Маркетплейс отклонил ключ — обновите его.',
  'integrations.attention.RATE_LIMITED': 'При последней проверке маркетплейс был недоступен — попробуйте ещё раз.',
  'integrations.attention.DOWN': 'При последней проверке маркетплейс был недоступен — попробуйте ещё раз.',
  'integrations.attention.DEGRADED': 'Последняя проверка не смогла подтвердить подключение.',
  'integrations.keyHint': 'Ключ оканчивается на ••••{last4}',
  'integrations.lastCheck': 'Последняя проверка: {date}',
  'integrations.lastSync': 'Последняя успешная загрузка заказов: {date}',
  'integrations.warehouseLabel': 'Склад фулфилмента: {name}',
  'integrations.help.WB': 'API-ключ категории «Маркетплейс»: токен из личного кабинета продавца → Настройки → Доступ к API. Токен категории «Возвраты» пока не поддерживается.',
  'integrations.help.OZON': 'Client ID и API-ключ из личного кабинета продавца → Настройки → API-ключи.',
  'integrations.help.YANDEX_MARKET': 'API-ключ, а также идентификатор бизнеса и идентификатор кампании вашего магазина.',
  'integrations.note.check': 'Ключ проверяется настоящим запросом к маркетплейсу при сохранении. Сохранение ключа само по себе не запускает загрузку заказов — загрузку заказов включает склад.',
  'integrations.note.storage': 'Ключ хранится в зашифрованном виде, а показываются только его последние 4 символа.',
  'integrations.field.apiKey': 'API-ключ',
  'integrations.field.clientId': 'Client ID',
  'integrations.field.businessId': 'ID бизнеса',
  'integrations.field.campaignId': 'ID кампании',
  'integrations.field.warehouse': 'Склад фулфилмента',
  'integrations.field.warehouseChoose': 'Выберите склад',
  'integrations.field.show': 'Показать',
  'integrations.field.hide': 'Скрыть',
  'integrations.noWarehouse': 'Склад ещё не оформил с вами договор, поэтому маркетплейс подключить нельзя. Обратитесь к менеджеру.',
  'integrations.action.connect': 'Подключить',
  'integrations.action.update': 'Обновить ключ',
  'integrations.action.check': 'Проверить подключение',
  'integrations.action.disconnect': 'Отключить',
  'integrations.action.save': 'Проверить и сохранить',
  'integrations.action.saveUpdate': 'Проверить и заменить ключ',
  'integrations.action.cancel': 'Отмена',
  'integrations.busy.save': 'Проверяем подключение…',
  'integrations.busy.check': 'Проверяем подключение…',
  'integrations.busy.disconnect': 'Отключаем…',
  'integrations.done.connected': 'Подключено. Маркетплейс принял ключ, он сохранён.',
  'integrations.done.updated': 'Маркетплейс принял новый ключ, он сохранён.',
  'integrations.done.checkOk': 'Маркетплейс подтвердил подключение.',
  'integrations.done.disconnected': 'Отключено. Ключ удалён, загрузка заказов остановлена.',
  'integrations.disconnectConfirm': 'Отключить {name}? Ключ будет удалён, загрузка заказов с этого маркетплейса остановится.',
  'integrations.err.required': 'Заполните это поле.',
  'integrations.err.field.apiKey': 'Введите API-ключ.',
  'integrations.err.field.clientId': 'Client ID должен состоять только из цифр.',
  'integrations.err.field.businessId': 'ID бизнеса должен состоять только из цифр.',
  'integrations.err.field.campaignId': 'ID кампании должен состоять только из цифр.',
  'integrations.err.inputInvalid': 'Проверьте введённые данные и попробуйте снова.',
  'integrations.err.keyRejected': 'Маркетплейс не принял ключ. Он может быть неверным, отозванным, просроченным или без нужного доступа — маркетплейс не сообщает, что именно. Ничего не сохранено.',
  'integrations.err.keyRejected.WB': 'Wildberries не принял токен. Проверьте, что он создан в разделе «Маркетплейс» (категория Marketplace), не просрочен и вставлен целиком. Ничего не сохранено.',
  'integrations.err.keyRejected.OZON': 'Ozon не принял ключ. Проверьте API-ключ и Client ID: они должны относиться к одному кабинету продавца. Ничего не сохранено.',
  'integrations.err.keyRejected.YANDEX_MARKET': 'Яндекс Маркет не принял ключ или он не относится к этой кампании. Проверьте API-ключ, ID бизнеса и ID кампании. Ничего не сохранено.',
  'integrations.err.requestRejected': 'Маркетплейс не принял проверку. Проверьте ключ и введённые идентификаторы. Ничего не сохранено.',
  'integrations.err.requestRejected.WB': 'Wildberries не принял проверку. Проверьте, что токен создан в разделе «Маркетплейс» (категория Marketplace), не просрочен и вставлен целиком. Ничего не сохранено.',
  'integrations.err.requestRejected.OZON': 'Ozon не принял проверку. Проверьте API-ключ и Client ID. Ничего не сохранено.',
  'integrations.err.requestRejected.YANDEX_MARKET': 'Яндекс Маркет не принял проверку. Проверьте ID бизнеса и ID кампании. Ничего не сохранено.',
  'integrations.err.marketplaceRateLimited': 'Маркетплейс сейчас ограничивает запросы. Подождите минуту и попробуйте снова. Ничего не сохранено.',
  'integrations.err.unavailable': 'Не удалось связаться с маркетплейсом. Попробуйте через несколько минут. Ничего не сохранено.',
  'integrations.err.notVerified': 'Ключ ещё не проверен. Сначала проверьте подключение.',
  'integrations.err.alreadyConnected': 'Этот маркетплейс уже подключён. Список обновлён.',
  'integrations.err.notConnected': 'Этот маркетплейс уже не подключён. Список обновлён.',
  'integrations.err.warehouseNone': 'Склад ещё не оформил с вами договор, поэтому маркетплейс подключить нельзя. Обратитесь к менеджеру.',
  'integrations.err.warehouseChoose': 'Выберите склад фулфилмента.',
  'integrations.err.ownerNotAllowed': 'Вы не можете подключать маркетплейс для этой компании.',
  'integrations.err.warehouseNotAllowed': 'Вы не можете использовать этот склад.',
  'integrations.err.tooMany': 'Слишком много проверок за короткое время. Попробуйте через минуту.',
  'integrations.err.tooLarge': 'Запрос слишком большой.',
  'integrations.err.session': 'Сессия истекла. Войдите снова.',
  'integrations.err.network': 'Соединение потеряно. Проверьте интернет и попробуйте снова.',
  'integrations.err.generic': 'Что-то пошло не так. Попробуйте ещё раз чуть позже.',
  'nav.companyOne': 'Ваша компания',
  'nav.companyMany': 'Ваши компании',
  'outStatus.DRAFT': 'Черновик',
  'outStatus.NEW': 'Отправлена складу',
  'outStatus.RESERVED': 'Зарезервирована',
  'outStatus.PICKING': 'Собирается',
  'outStatus.PICKED': 'Собрана',
  'outStatus.PACKED': 'Упакована',
  'outStatus.SHIPPED': 'Отгружена',
  'outStatus.CANCELLED_AFTER_PICK': 'Отменена (после сборки)',
  'outStatus.CANCELLED': 'Отменена',
  'outStatus.ON_HOLD': 'На паузе',
  'out.colNumber': 'Отгрузка',
  'out.colCarrier': 'Перевозчик и трек',
  'out.emptyHint': 'Создайте заявку, чтобы склад отправил ваш товар. Пока вы её не отправили, она остаётся черновиком.',
  'out.detailTitle': 'Отгрузка {number}',
  'out.recipient': 'Получатель',
  'out.result': 'Результат отгрузки',
  'out.resultTracking': 'Номер отслеживания',
  'out.noResultYet': 'Склад ещё не отгрузил этот заказ.',
  'out.statusFilterAll': 'Все',
  'drafts.actions': 'Действия',
  'drafts.edit': 'Изменить',
  'drafts.send': 'Отправить в работу',
  'drafts.sending': 'Отправляем…',
  'drafts.delete': 'Удалить черновик',
  'drafts.deleting': 'Удаляем…',
  'drafts.hint': 'Это черновик: его видите только вы. Склад увидит заявку, когда вы её отправите.',
  'drafts.in.submitConfirm': 'Отправить заявку складу? После отправки заявку нельзя удалить — только отменить.',
  'drafts.out.submitConfirm': 'Отправить заявку складу? Товар сразу зарезервируется. После отправки заявку нельзя удалить — только отменить.',
  'drafts.deleteConfirm': 'Удалить черновик? Это действие нельзя отменить.',
  'drafts.keptNote': 'Заявка уже отправлена складу и сохраняется в истории. Её можно отменить, пока склад не начал работу.',
  'drafts.keptNoteLate': 'Заявка отправлена складу и сохраняется в истории.',
  'drafts.sentOk': 'Заявка отправлена складу.',
  'drafts.savedKept': 'Черновик сохранён — вы можете исправить его и отправить позже.',
  'drafts.openDraft': 'Открыть черновик',
  'drafts.err.generic': 'Что-то пошло не так. Попробуйте ещё раз чуть позже.',
  'requests.saveDraft': 'Сохранить черновик',
  'requests.savingDraft': 'Сохраняем…',
  'requests.in.editTitle': 'Черновик приёмки',
  'requests.out.editTitle': 'Черновик отгрузки',
  'requests.err.notDraft': 'Заявка уже отправлена складу — её нельзя изменить или удалить.',
  'requests.err.shipToIncomplete': 'Заполните получателя (имя, адрес, город, страна) перед отправкой заявки.',
  'requests.in.stateHint.DRAFT': 'Черновик: его видите только вы. Отправьте складу, когда он будет готов.',
  'onboarding.NO_WAREHOUSE': 'Склад ещё не назначен вашему аккаунту. Обратитесь к менеджеру склада.',
  'onboarding.CONTRACT_NOT_ACTIVE': 'Договор ещё не активирован. После его оформления вы сможете отправлять заявки.',
  'onboarding.nextStep': 'Чтобы завершить настройку, свяжитесь с менеджером склада.',
  'requests.err.noWarehouse': 'Склад ещё не назначен вашему аккаунту. Обратитесь к менеджеру склада.',
  'requests.err.contractNotActive': 'Договор ещё не активирован. После его оформления вы сможете отправлять заявки.',
  'requests.warehouseSingle': 'Склад',
  'integrations.err.contractRequired': 'Склад ещё не оформил с вами договор, поэтому маркетплейс подключить нельзя. Обратитесь к менеджеру.',
  // Редактирование заявки после передачи на склад (ADR-089)
  'revise.lastEdited': 'Последнее изменение: {when}',
  'revise.lastEditedBy': 'Последнее изменение: {when} · {who}',
  'revise.neverEdited': 'Не менялась после передачи на склад',
  'revise.editedBadge': 'Изменено после передачи на склад',
  'revise.editedMark': 'изменено',
  'revise.action': 'Изменить заявку',
  'revise.title.in': 'Изменение заявки на приёмку',
  'revise.title.out': 'Изменение заявки на отгрузку',
  'revise.hint.in': 'Склад увидит каждое изменение сразу. Уже принятый складом товар из заявки убрать нельзя.',
  'revise.hint.out': 'Склад увидит каждое изменение сразу. При смене количества резерв ваших остатков обновляется сразу; если товара не хватает, ничего не меняется.',
  'revise.hint.header': 'Склад уже собирает этот заказ, поэтому состав изменить нельзя. Получателя, дату и номер изменить ещё можно.',
  'revise.save': 'Сохранить изменения',
  'revise.saving': 'Сохраняем…',
  'revise.confirm': 'Сохранить изменения? Склад увидит их сразу.',
  'revise.noChanges': 'Сохранять нечего — заявка не изменилась.',
  'revise.done': 'Изменения в заявке {number} сохранены.',
  'revise.doneHint': 'Склад теперь видит обновлённую заявку.',
  'revise.backToRequest': 'Вернуться к заявке',
  'revise.stale': 'Заявка уже была изменена. Откройте её текущую версию и внесите изменение заново.',
  'revise.openCurrent': 'Открыть текущую версию',
  'revise.conflictTitle': 'Изменение не применено. В заявке ничего не изменилось:',
  'revise.conflict.OVERAGE_PENDING': 'По {sku} принято {received}, больше заявленного; склад ещё решает по излишку. Количество {requested} пока нельзя указать. Свяжитесь со складом.',
  'revise.conflict.BELOW_RECEIVED': 'Уже принято {received} ({sku}), нельзя уменьшить до {requested}. Свяжитесь со складом.',
  'revise.conflict.REMOVE_RECEIVED': '{sku}: уже принято {received}, поэтому строку нельзя удалить. Свяжитесь со складом.',
  'revise.conflict.SPLIT_LINES': '{sku}: склад разбил этот товар на несколько строк. Чтобы изменить его, свяжитесь со складом.',
  'revise.conflict.PICKING_STARTED': 'Склад уже собирает заказ, поэтому {sku} изменить нельзя. Свяжитесь со складом.',
  'revise.conflict.ALREADY_PICKED': '{sku}: уже собрано {received}, поэтому строку изменить нельзя. Свяжитесь со складом.',
  'revise.err.notEditable': 'Эту заявку уже нельзя изменить — склад уже обработал или закрыл её.',
  'revise.history': 'История изменений',
  'revise.rev': 'Редакция {n}',
  'revise.revSubmitted': 'Передана на склад',
  'revise.byWarehouse': 'склад',
  'revise.line.ADDED': '{sku}: добавлено, количество {to}',
  'revise.line.REMOVED': '{sku}: удалено (было {from})',
  'revise.line.CHANGED': 'Количество {sku}: {from} → {to}',
  'revise.fieldChange': '{field}: {from} → {to}',
  'revise.f.reference': 'Номер',
  'revise.f.expectedAt': 'Ожидаемая дата',
  'revise.f.dueAt': 'Дата',
  'revise.f.shipTo.name': 'Получатель',
  'revise.f.shipTo.phone': 'Телефон',
  'revise.f.shipTo.email': 'Эл. почта',
  'revise.f.shipTo.address': 'Адрес',
  'revise.f.shipTo.city': 'Город',
  'revise.f.shipTo.postalCode': 'Индекс',
  'revise.f.shipTo.country': 'Страна',
  'revise.f.shipTo.comment': 'Комментарий',
  'requests.out.fulfilment': 'Способ исполнения заказа',
  'requests.out.fulfilmentStandard': 'Стандартный',
  'requests.out.fulfilmentStandardHint': 'Обычная отгрузка: склад собирает товар и отправляет его получателю.',
  'requests.out.fulfilmentFbs': 'FBS',
  'requests.out.fulfilmentFbsHint': 'Заказ маркетплейса, исполняемый силами склада: склад собирает, упаковывает и передаёт его для маркетплейса.',
  'requests.out.fulfilmentRequired': 'Чтобы продолжить, выберите способ исполнения заказа.',
  'requests.out.fulfilmentUnknown': 'Не указан',
};
